import { useTranslation } from 'react-i18next';
import { ChevronRight, Mic, ImageIcon } from 'lucide-react';
import { cn, getInitials, truncateText } from '@/lib/utils';
import { Badge } from '@/components/atoms/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/atoms/avatar';
import type { Conversation } from '@/lib/types/messaging';

interface ConversationListItemProps {
  conversation: Conversation;
  onClick: () => void;
  isSelected?: boolean;
}

const statusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  active: { label: 'Actif', variant: 'default' },
  disputed: { label: 'Litige', variant: 'destructive' },
  resolved: { label: 'Résolu', variant: 'secondary' },
  closed: { label: 'Fermé', variant: 'outline' },
  expired: { label: 'Expiré', variant: 'outline' },
};

export function ConversationListItem({
  conversation,
  onClick,
  isSelected = false,
}: ConversationListItemProps) {
  const { t } = useTranslation();
  const hasUnread = conversation.unreadCount > 0;
  const status = statusConfig[conversation.status] || statusConfig.active;

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return t('common.yesterday', 'Hier');
    } else if (diffDays < 7) {
      return date.toLocaleDateString('fr-FR', { weekday: 'short' });
    }
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const getLastMessagePreview = () => {
    if (!conversation.lastMessage) return null;

    const msg = conversation.lastMessage;
    if (msg.type === 'audio') {
      return (
        <span className="flex items-center gap-1">
          <Mic className="size-3" />
          {t('messaging.voiceMessage', 'Message vocal')}
        </span>
      );
    }
    if (msg.type === 'image') {
      return (
        <span className="flex items-center gap-1">
          <ImageIcon className="size-3" />
          {msg.content || t('messaging.photo', 'Photo')}
        </span>
      );
    }
    return truncateText(msg.content || '', 40);
  };

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors',
        'border-b border-border hover:bg-accent/50',
        isSelected && 'bg-accent',
        hasUnread && 'bg-primary/5'
      )}
    >
      <div className="relative">
        <Avatar className="size-12">
          <AvatarImage src={conversation.customer.avatarUrl || undefined} />
          <AvatarFallback className="bg-primary/10 text-primary font-medium">
            {getInitials(conversation.customer.name)}
          </AvatarFallback>
        </Avatar>
        {hasUnread && (
          <span className="absolute -top-1 -right-1 size-5 bg-primary text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-background">
            {conversation.unreadCount > 9 ? '9+' : conversation.unreadCount}
          </span>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span
            className={cn(
              'text-sm truncate',
              hasUnread ? 'font-semibold' : 'font-medium'
            )}
          >
            {conversation.customer.name}
          </span>
          <span className="text-[10px] text-muted-foreground shrink-0">
            {formatTime(conversation.lastMessage?.createdAt || conversation.updatedAt)}
          </span>
        </div>

        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-xs text-muted-foreground">
            #{conversation.order.orderNumber}
          </span>
          <Badge variant={status.variant} className="text-[10px] px-1.5 py-0">
            {status.label}
          </Badge>
        </div>

        {conversation.lastMessage && (
          <p
            className={cn(
              'text-xs mt-1 truncate',
              hasUnread ? 'text-foreground font-medium' : 'text-muted-foreground'
            )}
          >
            {getLastMessagePreview()}
          </p>
        )}
      </div>

      <ChevronRight className="size-5 text-muted-foreground shrink-0" />
    </button>
  );
}
